import React, { useCallback, useEffect, useState } from 'react';
import { FaCommentAlt, FaTimes } from 'react-icons/fa';
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from 'react-icons/md';
import { InternalFieldName, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import CustomButton from '../CustomButton/CustomButton';

export enum EstimateCommentType {
  General = 1,
  Internal,
}

interface EstimateCommentsProps {
  estimateId: number;
  type: EstimateCommentType;
}

interface EstimateCommentFormFields {
  comment: string;
}

type EstimateCommentModel = {
  id: number;
  comment: string;
  userName: number;
  createdAt: string;
};

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function EstimateComments({
  estimateId,
  type,
}: EstimateCommentsProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    setValue,
    setFocus,
  } = useForm<EstimateCommentFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [isShowComments, setIsShowComments] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [route, setRoute] = useState<string>('');
  const [label, setLabel] = useState<string>('Observação');
  const [pluralLabel, setPluralLabel] = useState<string>('Observações');
  const [estimateComments, setEstimateComments] = useState<
    EstimateCommentModel[]
  >([]);

  const {
    user: { organization },
  } = useAuth();

  const setTextAreaHeight = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'inherit';
    e.target.style.height = `${e.target.scrollHeight + 2}px`;
  };

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getComments() {
    if (organization && estimateId && !!route) {
      setIsLoading(true);
      const { data } = await api.get(
        `organizations/${organization.id}/estimates/${estimateId}/comments/${route}`
      );
      setEstimateComments(data.data);
      setIsLoading(false);
    }
  }

  const removeComment = useCallback(
    async (estimateComment: EstimateCommentModel) => {
      try {
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover a ${label} "${estimateComment.comment}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/estimates/${estimateId}/comments/${estimateComment.id}`
          );
          getComments();
          toast.success(
            `A ${label} "${estimateComment.comment}" foi removida do Orçamento`
          );
        }
      } catch (e) {
        toast.error(
          `Houve um problema ao tentar remover a ${label} do Orçamento. Tente novamente.`
        );
      }
    },
    [organization.id, estimateId, getComments, toast]
  );

  const onSubmit = async (data: EstimateCommentFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/estimates/${estimateId}/comments`,
        { ...data, type }
      );

      setValue('comment', '');
      setFocus('comment');
      getComments();
      toast.success(`A ${label} foi adicionada ao Orçamento`);
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  useEffect(() => {
    if (type === EstimateCommentType.General) {
      setRoute('general');
    } else if (type === EstimateCommentType.Internal) {
      setRoute('internal');
      setLabel('Observação Interna');
      setPluralLabel('Observações Internas (Não serão exibidas no PDF)');
    }
  }, [type]);

  useEffect(() => {
    getComments();
  }, [organization, estimateId, route]);

  return (
    <div>
      <div className='mt-3'>
        <div>
          {isShowComments ? (
            <button
              className='btn btn-sm'
              type='button'
              onClick={() => setIsShowComments(false)}
            >
              <MdOutlineKeyboardArrowUp size={30} /> Ocultar {pluralLabel}
            </button>
          ) : (
            <button
              className='btn btn-sm'
              type='button'
              onClick={() => setIsShowComments(true)}
            >
              <MdOutlineKeyboardArrowDown size={30} /> Mostrar {pluralLabel} (
              {estimateComments.length})
            </button>
          )}
        </div>
        {isShowComments &&
          (isLoading ? (
            <div className='placeholder-glow'>
              <span className='placeholder placeholder-lg col-12' />
              <span className='placeholder placeholder-xs col-12 bg-dark' />
              <span className='placeholder col-12 bg-secondary' />
            </div>
          ) : (
            estimateComments.map((item) => {
              const createdAt = new Date(item.createdAt);
              return (
                <div
                  key={item.id}
                  className='d-flex flex-column border rounded-1 mb-2 p-2 bg-light'
                >
                  <div className='d-flex align-items-start justify-content-between gap-1'>
                    <div className='text-break'>
                      <span>{item.comment}</span>
                    </div>
                    <button
                      className='btn btn-sm p-0'
                      type='button'
                      onClick={() => removeComment(item)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className='d-flex flex-column align-items-end'>
                    <small className='text-muted'>{item.userName}</small>
                    <small className='text-muted'>
                      {createdAt.toLocaleDateString()}&nbsp;
                      {createdAt.toLocaleTimeString()}
                    </small>
                  </div>
                </div>
              );
            })
          ))}
      </div>
      <div>
        <form>
          <div className='mt-3'>
            <textarea
              {...register('comment', { required: true })}
              className='form-control'
              placeholder={`Adicione uma nova ${label}`}
              required
              onChange={setTextAreaHeight}
              onFocus={setTextAreaHeight}
            />
            {errors.comment && (
              <div className='form-text text-danger'>
                {errors.comment.message}
              </div>
            )}
          </div>
          <div className='text-end mt-3'>
            <CustomButton
              type='submit'
              className='btn btn-sm btn-secondary'
              handleClick={handleSubmit(onSubmit)}
            >
              Adicionar {label} <FaCommentAlt />
            </CustomButton>
          </div>
        </form>
      </div>
    </div>
  );
}
